<script setup lang="ts">
  import { testIds } from '@gat-ship/types'
  import {
    isStakeholderContactPerson,
    isStakeholderContactOther
  } from '@gat-ship/types'
  import { useContactProvider } from '../../../composables/useStakeholderContacts'
  import {
    Button,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DataList,
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
    SectionHeader
  } from '@gat-ship/vue-components'
  import { IconPlus, IconUsers } from '@gat-ship/icons'
  import { useAppLoading } from '@gat-ship/vue-components'
  import ContactPersonBtnAdd from '../commands/buttons/ContactPersonBtnAdd.vue'
  import ContactPointBtnAdd from '../commands/buttons/ContactPointBtnAdd.vue'
  import ContactPersonListItem from './ContactPersonListItem.vue'
  import ContactPointListItem from './ContactPointListItem.vue'
  import { computed, inject } from 'vue'
  import { text } from '@gat-ship/stakeholders'
  import { stakeholderKey } from '@gat-ship/types/injection-keys'
  import { useStakeholderPermissions } from '@gat-ship/stakeholders'
  import SmartPasteBtnMultipleContacts from '../smart-paste/SmartPasteBtnMultipleContacts.vue'

  defineOptions({
    inheritAttrs: false
  })

  const props = withDefaults(
    defineProps<{
      showAddContactPersonBtn?: boolean
      showAddContactOtherBtn?: boolean
    }>(),
    {
      showAddContactPersonBtn: true,
      showAddContactOtherBtn: true
    }
  )

  const contactsProvider = useContactProvider()

  const service = inject(stakeholderKey)

  const { isLoading } = useAppLoading()

  const stakeholder = computed(() => service?.stakeholder.value)

  const { canAddContact } = useStakeholderPermissions()

  // Adding contacts (person, point, or via smart-paste) needs the addContact
  // permission and an active stakeholder. Existing contacts stay readable.
  const canAddContacts = computed(() =>
    stakeholder.value ? canAddContact(stakeholder.value) : false
  )

  const showContactType = computed(() => {
    return (
      contactsProvider.contacts.value.some((contact) =>
        isStakeholderContactPerson(contact)
      ) &&
      contactsProvider.contacts.value.some((contact) =>
        isStakeholderContactOther(contact)
      )
    )
  })
</script>
<template>
  <SectionHeader
    :title="text.views.contacts.pageHeader.title"
    :description="
      text.views.contacts.pageHeader.description(stakeholder?.name ?? '')
    "
  >
    <template #actions>
      <DropdownMenu v-if="canAddContacts">
        <DropdownMenuTrigger as-child>
          <Button
            class="ml-auto w-fit"
            :data-testid="testIds.stakeholders.contacts.actions.addDropdown"
          >
            <IconPlus class="size-4" />
            Add...
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <ContactPersonBtnAdd
            v-if="props.showAddContactPersonBtn && stakeholder"
            :stakeholder-id="stakeholder.id"
            :stakeholder-name="stakeholder.name"
            :stakeholder-short-name="stakeholder.shortName ?? ''"
            :stakeholder-type="stakeholder.stakeholderType"
            label="Contact Person"
          />
          <ContactPointBtnAdd
            v-if="props.showAddContactOtherBtn && stakeholder"
            :stakeholder-id="stakeholder.id"
            :stakeholder-name="stakeholder.name"
            :stakeholder-short-name="stakeholder.shortName ?? ''"
            :stakeholder-type="stakeholder.stakeholderType"
            label="Contact Point"
          />
        </DropdownMenuContent>
      </DropdownMenu>
      <SmartPasteBtnMultipleContacts
        v-if="stakeholder && canAddContacts"
        :stakeholder-id="stakeholder.id"
        :stakeholder-name="stakeholder.name"
        :stakeholder-short-name="stakeholder.shortName ?? ''"
        :stakeholder-type="stakeholder.stakeholderType"
      />
    </template>
  </SectionHeader>

  <DataList
    :loading="isLoading('loading-contacts')"
    :empty="contactsProvider.contacts.value.length === 0"
  >
    <template
      v-for="contact in contactsProvider.contacts.value"
      :key="contact.id"
    >
      <ContactPersonListItem
        v-if="isStakeholderContactPerson(contact) && stakeholder"
        :contact="contact"
        :show-contact-type="showContactType"
        @click:item="
          contactsProvider.modals.showViewContactModal({
            stakeholderId: stakeholder.id,
            stakeholderName: stakeholder.name,
            stakeholderShortName: stakeholder.shortName ?? '',
            stakeholderType: stakeholder.stakeholderType,
            contactType: contact.contactType,
            contact: contact
          })
        "
      />
      <ContactPointListItem
        v-if="isStakeholderContactOther(contact) && stakeholder"
        :contact="contact"
        :show-contact-type="showContactType"
        @click:item="
          contactsProvider.modals.showViewContactModal({
            stakeholderId: stakeholder.id,
            stakeholderName: stakeholder.name,
            stakeholderShortName: stakeholder.shortName ?? '',
            stakeholderType: stakeholder.stakeholderType,
            contactType: contact.contactType,
            contact: contact
          })
        "
      />
    </template>
    <template #empty>
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <IconUsers />
          </EmptyMedia>
          <EmptyTitle>No contacts yet</EmptyTitle>
          <EmptyDescription>
            Contact persons and contact points added to this stakeholder will
            show up here.
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent
          v-if="
            stakeholder &&
            canAddContacts &&
            (props.showAddContactPersonBtn || props.showAddContactOtherBtn)
          "
        >
          <div class="flex items-center gap-4">
            <ContactPersonBtnAdd
              v-if="props.showAddContactPersonBtn"
              as="empty-state"
              :stakeholder-id="stakeholder.id"
              :stakeholder-name="stakeholder.name"
              :stakeholder-short-name="stakeholder.shortName ?? ''"
              :stakeholder-type="stakeholder.stakeholderType"
            />
            <span
              v-if="
                props.showAddContactPersonBtn && props.showAddContactOtherBtn
              "
              class="text-muted-foreground text-xs uppercase"
            >
              or
            </span>
            <ContactPointBtnAdd
              v-if="props.showAddContactOtherBtn"
              as="empty-state"
              :stakeholder-id="stakeholder.id"
              :stakeholder-name="stakeholder.name"
              :stakeholder-short-name="stakeholder.shortName ?? ''"
              :stakeholder-type="stakeholder.stakeholderType"
            />
          </div>
        </EmptyContent>
      </Empty>
    </template>
  </DataList>
</template>
